import { useMutation, type UseMutationResult } from '@tanstack/react-query';
import {
  createInitiativeRequestSchema,
  initiativeWriteResponseSchema,
  updateInitiativeRequestSchema,
  type FieldConflict,
  type InitiativeWriteInput,
  type InitiativeWriteResponse,
} from '@complan/contracts';
import { apiFetch } from '@/shared/api/client';
import { ApiRequestError } from '@/shared/api/api-error';
import { useInvalidateCalendar } from '@/features/calendar/use-calendar-freshness';
import { useInvalidateManpowerViews } from '@/features/manpower/use-invalidate-manpower-views';
export { useIntakeOptions, type IntakeOptionsResult, type ReferencedOptionIds } from './intake-options-data';

/**
 * Data wiring for the intake wizard.
 *
 * ## Options load
 *
 * The controlled-value option sets are loaded by `useIntakeOptions` in
 * `intake-options-data.ts`, from the reference-data endpoints
 * (`GET /api/users/selectable`, `GET /api/master-data/:set/selectable`) rather
 * than from the administration routes - those require `masterdata:admin`, which
 * the role that fills in an Initiative does not hold. See that module for the full
 * reasoning and for how R5.9's retained-inactive values are requested.
 */

/**
 * `POST /api/initiatives` — create with the requested status or Draft (R5.6, R9.1).
 * The payload is parsed through the contract create schema so a malformed field
 * fails at the boundary rather than on the wire.
 */
export function useCreateInitiative(): UseMutationResult<
  InitiativeWriteResponse,
  unknown,
  InitiativeWriteInput
> {
  // The acting user's own create must appear on their calendar within 3 s without
  // a reload (R13.3): invalidate the calendar read on success so the new bar is
  // rendered from the fresh server read (task 22.7).
  const invalidateCalendar = useInvalidateCalendar();
  // Invalidate manpower views when field force demand inputs are created (task 14.4)
  const invalidateManpower = useInvalidateManpowerViews();
  return useMutation({
    mutationFn: async (input: InitiativeWriteInput): Promise<InitiativeWriteResponse> => {
      const body = createInitiativeRequestSchema.parse({ ...input, requestedStatus: null });
      const response = await apiFetch<unknown>('/api/initiatives', {
        method: 'POST',
        body: JSON.stringify(body),
      });
      return initiativeWriteResponseSchema.parse(response);
    },
    onSuccess: () => {
      invalidateCalendar();
      void invalidateManpower();
    },
  });
}

/**
 * `PUT /api/initiatives/:id` — update an existing record against its loaded
 * `version` (R4.9, R5.8). A version mismatch is answered by the server with a
 * `409 conflict` carrying the per-field stored-vs-submitted diff; there is no
 * automatic retry. The mutation surfaces that error as an {@link ApiRequestError}
 * so the caller can render the conflict dialog with {@link extractVersionConflict}
 * while keeping the user's entered values in the form.
 */
export function useUpdateInitiative(
  initiativeId: string,
  loadedVersion: number,
): UseMutationResult<InitiativeWriteResponse, unknown, InitiativeWriteInput> {
  const invalidateCalendar = useInvalidateCalendar();
  // Invalidate manpower views when field force demand inputs are edited (task 14.4)
  const invalidateManpower = useInvalidateManpowerViews();
  return useMutation({
    mutationFn: async (input: InitiativeWriteInput): Promise<InitiativeWriteResponse> => {
      const body = updateInitiativeRequestSchema.parse({ ...input, version: loadedVersion });
      const response = await apiFetch<unknown>(`/api/initiatives/${initiativeId}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      return initiativeWriteResponseSchema.parse(response);
    },
    onSuccess: () => {
      invalidateCalendar();
      void invalidateManpower();
    },
  });
}

/**
 * The version-conflict diff of R5.8, extracted from a thrown mutation error.
 *
 * A `PUT` rejected for a stale version throws an {@link ApiRequestError} whose
 * body is a `conflict` with a `version` detail carrying the per-field diff. Any
 * other error (a transition conflict, a stale preview, a non-conflict failure)
 * returns `null`, so the caller renders the conflict dialog only for a genuine
 * version mismatch and lets the error boundary handle the rest. The user's
 * entered form values are never touched here (R5.8).
 */
export function extractVersionConflict(error: unknown): VersionConflict | null {
  if (!(error instanceof ApiRequestError)) return null;
  const body = error.body;
  if (body.code !== 'conflict' || body.conflict.kind !== 'version') return null;
  return {
    storedVersion: body.conflict.storedVersion,
    submittedVersion: body.conflict.submittedVersion,
    fields: body.conflict.fields,
  };
}

/** The version-conflict diff the conflict dialog renders (R5.8). */
export interface VersionConflict {
  readonly storedVersion: number;
  readonly submittedVersion: number;
  readonly fields: readonly FieldConflict[];
}
